import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import proxy from "../proxy";
import {
  Box,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  HStack,
  Icon,
  Skeleton,
} from "@chakra-ui/react";
import { ArrowForwardIcon, TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import useStockPrices from "../hooks/useStockPrices";

// KPI cu valoarea portofoliului + profitul nerealizat (capital P/L)
// Aceeasi formula ca in Stocks.jsx (totalValue - totalInvested)
const PortfolioValueKPI = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState(null);
  const { prices } = useStockPrices();
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetStocks.php`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => setStocks(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStocks([]));
  }, []);

  const { totalValue, totalInvested, capitalPL, capitalPLPct } = useMemo(() => {
    if (!stocks || stocks.length === 0) {
      return { totalValue: 0, totalInvested: 0, capitalPL: 0, capitalPLPct: 0 };
    }

    const totalInvested = stocks.reduce(
      (sum, s) => sum + s.average_price_share * s.nr_shares,
      0
    );
    const totalValue = stocks.reduce((sum, s) => {
      const price = prices[s.ticker] || s.average_price_share;
      return sum + price * s.nr_shares;
    }, 0);
    const capitalPL = totalValue - totalInvested;

    return {
      totalValue,
      totalInvested,
      capitalPL,
      capitalPLPct: totalInvested > 0 ? (capitalPL / totalInvested) * 100 : 0,
    };
  }, [stocks, prices]);

  const fmtMoney = (n) =>
    `${n < 0 ? "−" : ""}$${Math.abs(n).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  if (!stocks) {
    return (
      <Box maxW="900px" mx="auto" mt={{ base: 3, md: 4 }} px={{ base: 2, md: 3 }}>
        <Skeleton height="120px" borderRadius="xl" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
      </Box>
    );
  }

  const isProfit = capitalPL >= 0;
  const color = isProfit ? "#48BB78" : "#F56565";
  const gradient = isProfit
    ? "linear-gradient(135deg,#48BB78,#68D391)"
    : "linear-gradient(135deg,#F56565,#FC8181)";

  return (
    <Box maxW="900px" mx="auto" mt={{ base: 3, md: 4 }} px={{ base: 2, md: 3 }}>
      <Box
        position="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => navigate("/stocks")}
        cursor="pointer"
      >
        {/* Glow */}
        <Box
          position="absolute"
          inset="-3px"
          borderRadius="xl"
          bg={gradient}
          opacity={hovered ? 0.3 : 0}
          filter="blur(18px)"
          transition="0.35s"
        />

        <Box
          bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
          p={{ base: 3, sm: 4, md: 5 }}
          borderRadius="xl"
          border="2px solid"
          borderColor={hovered ? color : "rgba(72,187,120,0.25)"}
          transition="0.35s"
          transform={hovered ? "translateY(-4px)" : "none"}
          boxShadow={hovered ? `0 20px 40px ${color}44` : "0 10px 25px rgba(0,0,0,0.35)"}
          position="relative"
        >
          <HStack justify="space-between" mb={{ base: 2, md: 3 }}>
            <Text
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
            >
              💼{" "}
              <Text as="span" display={{ base: "none", md: "inline" }}>
                Portfolio Value
              </Text>
              <Text as="span" display={{ base: "inline", md: "none" }}>
                Portfolio
              </Text>
            </Text>
            <ArrowForwardIcon
              color={color}
              opacity={hovered ? 1 : 0}
              transform={hovered ? "translateX(0)" : "translateX(-5px)"}
              transition="0.25s"
              boxSize={{ base: 3, md: 4 }}
            />
          </HStack>

          <Grid templateColumns="repeat(3, 1fr)" gap={{ base: 2, md: 4 }}>
            {/* Valoarea actuala de piata */}
            <Stat>
              <StatLabel color="gray.400" fontSize={{ base: "2xs", md: "xs" }} noOfLines={1}>
                Current Value
              </StatLabel>
              <StatNumber
                fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
                fontWeight="black"
                color="blue.300"
                lineHeight="1.2"
              >
                {fmtMoney(totalValue)}
              </StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: "2xs", md: "xs" }} mb={0}>
                market price
              </StatHelpText>
            </Stat>

            {/* Capital investit */}
            <Stat>
              <StatLabel color="gray.400" fontSize={{ base: "2xs", md: "xs" }} noOfLines={1}>
                Invested
              </StatLabel>
              <StatNumber
                fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
                fontWeight="black"
                color="gray.300"
                lineHeight="1.2"
              >
                {fmtMoney(totalInvested)}
              </StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: "2xs", md: "xs" }} mb={0}>
                avg. buy price
              </StatHelpText>
            </Stat>

            {/* Profit nerealizat (capital P/L) */}
            <Stat>
              <StatLabel color="gray.400" fontSize={{ base: "2xs", md: "xs" }} noOfLines={1}>
                Unrealized P/L
              </StatLabel>
              <StatNumber
                fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
                fontWeight="black"
                background={gradient}
                backgroundClip="text"
                sx={{ WebkitTextFillColor: "transparent" }}
                textShadow={`0 0 20px ${color}33`}
                lineHeight="1.2"
              >
                {fmtMoney(capitalPL)}
              </StatNumber>
              <StatHelpText color={isProfit ? "green.400" : "red.400"} fontSize={{ base: "2xs", md: "xs" }} mb={0}>
                <Icon
                  as={isProfit ? TriangleUpIcon : TriangleDownIcon}
                  boxSize={2.5}
                  mr={1}
                />
                {isProfit ? "+" : "−"}{Math.abs(capitalPLPct).toFixed(2)}%
              </StatHelpText>
            </Stat>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default PortfolioValueKPI;
